// TotalsRow - Renders a totals/summary row at the bottom of the table
// Allows selecting aggregation type per column (Sum, Count, None)

import React from 'react';
import type { TableColumn, TotalsRowConfig, AggregationType } from '@/app/types/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/app/components/ui/select';
import { isAggregationCompatible, formatAggregation } from '@/app/utils/aggregationUtils';

interface TotalsRowProps {
  columns: TableColumn[];
  totalsRowConfig: TotalsRowConfig;
  totalsRowData: { [columnId: string]: number | string }; // Calculated values
  onUpdateAggregation: (columnId: string, aggregation: AggregationType) => void;
}

export function TotalsRow({ 
  columns, 
  totalsRowConfig, 
  totalsRowData,
  onUpdateAggregation 
}: TotalsRowProps) {
  if (!totalsRowConfig.enabled) {
    return null;
  }

  return (
    <tr style={{ backgroundColor: 'var(--md-surface-container-high)' }}>
      {columns.map((col, idx) => {
        const currentAgg = totalsRowConfig.aggregations[col.id] || 'none';
        const value = totalsRowData[col.id];
        
        return (
          <td
            key={col.id}
            style={{
              width: `${col.width}px`,
              minWidth: `${col.width}px`,
              maxWidth: `${col.width}px`,
              padding: '8px',
              borderTop: '2px solid var(--md-outline)',
              borderRight: '1px solid var(--md-outline-variant)',
              fontWeight: 'bold',
              backgroundColor: 'var(--md-surface-container-high)',
            }}
          >
            <div className="flex items-center gap-2">
              {idx === 0 ? (
                // First column shows label
                <span style={{ color: 'var(--md-on-surface)' }}>
                  {totalsRowConfig.label || 'Summe'}
                </span>
              ) : (
                <>
                  {/* Aggregation dropdown */}
                  <Select
                    value={currentAgg}
                    onValueChange={(val) => onUpdateAggregation(col.id, val as AggregationType)}
                  >
                    <SelectTrigger className="h-7 text-xs min-w-[90px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">-</SelectItem>
                      {isAggregationCompatible(col.dataType, 'sum') && (
                        <SelectItem value="sum">Summe</SelectItem>
                      )}
                      <SelectItem value="count">Anzahl</SelectItem>
                      {isAggregationCompatible(col.dataType, 'avg') && (
                        <SelectItem value="avg">Ø</SelectItem>
                      )}
                      {isAggregationCompatible(col.dataType, 'min') && (
                        <SelectItem value="min">Min</SelectItem>
                      )}
                      {isAggregationCompatible(col.dataType, 'max') && (
                        <SelectItem value="max">Max</SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                  
                  {/* Display value */}
                  {currentAgg !== 'none' && value !== undefined && value !== '' && (
                    <span className="ml-2 font-mono" style={{ color: 'var(--md-primary)' }}>
                      {formatAggregation(value, currentAgg, col.dataType)}
                    </span>
                  )}
                </>
              )}
            </div>
          </td>
        );
      })}
    </tr>
  );
}